// Imports
import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components/macro';
import { fieldContainerBottomMargin } from '../../foundation/front-end/components/forms/field-container';
import FieldErrorMessage from '../../foundation/front-end/components/forms/field-error-message';
import Radio from '../../foundation/front-end/components/forms/fields/radio';
import theme from '../../theme';
import { GetSubscriptionResponse } from '../../types/api/main/reseller/website/subscription/get';
import availableSubscriptionSizes from '../../utils/billing/available-subscription-sizes';
import determineEmailAccountLimit from '../../utils/billing/determine-email-account-limit';
import planMapping from '../../utils/billing/plan-mapping';
import PlanCard from './plan-card';
import { generateDisplayedPlans } from './generate-displayed-plans';


// Styled components
const Wrapper = styled.div`
	&:not(:last-child) {
		margin-bottom: ${fieldContainerBottomMargin};
	}
	
	input[type='radio'] {
		position: absolute;
		opacity: 0;
	}
	
	fieldset {
		display: grid;
		grid-auto-flow: column;
		justify-content: start;
		align-items: flex-start;
		gap: 0.625rem;
		
		&:not(:last-child) {
			margin-bottom: 0;
		}
		
		@media (max-width: ${theme.breakpoints.desktop}px) {
			grid-auto-flow: row;
			align-items: center;
			gap: 0.125rem;
		}
	}
`;

const NoWrap = styled.span`
	white-space: nowrap;
`;


// Define the accepted props
interface Props {
	fieldLabel: string;
	currentUsage?: {
		size: number;
		emailAccounts: number;
		admins: number;
	};
	handleSelectedPlanChange: (newValue: number) => void;
	selectedPlan: number;
	storybookSelectedPlan?: number;
	selectedPlanChanged: boolean;
	errorMessage?: string;
	subscriptionPlan?: GetSubscriptionResponse['plan'];
	effectivePlan?: GetSubscriptionResponse['eligibilityFactors']['effectiveDetails']['plan'];
	resetKey?: number;
	displayDeluxePlan?: boolean;
}


// Function component
const PlanSelector: React.FC<Props> = ({
	currentUsage,
	handleSelectedPlanChange,
	selectedPlan,
	storybookSelectedPlan,
	selectedPlanChanged,
	errorMessage,
	subscriptionPlan,
	fieldLabel,
	effectivePlan,
	resetKey,
	displayDeluxePlan,
}) => {
	// Use ref
	const planSetter = useRef<(value: string) => void>();
	
	
	// Reset input if required
	useEffect(() => {
		if (planSetter.current && resetKey) {
			planSetter.current('');
		}
	}, [resetKey]);
	
	
	// Build plan choices
	const planChoices = useMemo(() => {
		// Initialize
		let disabledReason: {
			label: string;
			amount: number;
		};
		
		
		// Build pill content
		const generatePillContent = (planNumber: number, isChosen: boolean) => {
			// Handle if Inform plan should be disabled
			if (planNumber === planMapping.inform && currentUsage) {
				const maxInformSize = Math.max(...availableSubscriptionSizes(planMapping.inform));
				const maxInformEmailAccounts = determineEmailAccountLimit({ plan: planMapping.inform, size: maxInformSize });
				const maxInformAdminCount = 1;
				
				if (currentUsage.size > maxInformSize) {
					disabledReason = { label: 'accounts', amount: currentUsage.size };
				} else if (currentUsage.emailAccounts > maxInformEmailAccounts) {
					disabledReason = { label: 'email accounts', amount: currentUsage.emailAccounts };
				} else if (currentUsage.admins > maxInformAdminCount) {
					disabledReason = { label: 'admins', amount: currentUsage.admins };
				}
				
				if (disabledReason) {
					const { label, amount } = disabledReason;
					const formattedAmount = amount.toLocaleString();
					
					if (label === 'email accounts') {
						return (
							<React.Fragment>
								Too many email
								<br />
								accounts ({formattedAmount})
							</React.Fragment>
						);
					}
					
					if (amount < 1000) {
						return (
							<NoWrap>
								Too many {label} ({formattedAmount})
							</NoWrap>
						);
					} else {
						return (
							<React.Fragment>
								Too many
								<br />
								{label} ({formattedAmount})
							</React.Fragment>
						);
					}
				}
			}
			
			
			// Handle websites that already have a subscription
			if (subscriptionPlan) {
				if (
					planNumber === subscriptionPlan &&
					subscriptionPlan !== effectivePlan &&
					planNumber !== planMapping.grandfatheredDeluxe
				) {
					return 'Renewal plan';
				} else if (planNumber === subscriptionPlan) {
					return 'Current plan';
				} else if (planNumber < subscriptionPlan) {
					return 'Downgrade plan';
				} else {
					return 'Upgrade plan';
				}
			}
			
			
			// Handle other cases
			return isChosen ? 'Chosen plan' : 'Choose plan';
		};
		
		
		// Generate displayed plans
		const displayedPlans = generateDisplayedPlans(displayDeluxePlan);
		
		
		// Return choices
		return displayedPlans.map((plan) => {
			// Initialize
			const isChosen =
				(selectedPlan === plan.planNumber && selectedPlanChanged) ||
				(subscriptionPlan === plan.planNumber && !selectedPlanChanged);
			
			const pillContent = generatePillContent(plan.planNumber, isChosen);
			const isDisabled = plan.planNumber === planMapping.inform && Boolean(disabledReason);
			
			
			// Return
			return {
				label: (
					<PlanCard
						plan={plan}
						isDisabled={isDisabled}
						pillContent={pillContent}
						isChecked={isChosen}
						isFaded={isDisabled && Boolean(displayDeluxePlan)}
					/>
				),
				value: String(plan.planNumber),
				disabled: isDisabled,
			};
		});
	}, [selectedPlan, selectedPlanChanged, currentUsage, subscriptionPlan, effectivePlan, displayDeluxePlan]);
	
	
	// Return JSX
	return (
		<Wrapper>
			<Radio
				name='plan'
				label={fieldLabel}
				note={
					// eslint-disable-next-line
					<a href={`https://www.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/pricing`} target='_blank'>
						Compare plans
					</a>
				}
				choices={planChoices}
				onChange={(newValue) => {
					handleSelectedPlanChange(Number(newValue));
				}}
				initialValue={(subscriptionPlan ?? storybookSelectedPlan)?.toString()}
				bindSetter={(setter) => (planSetter.current = setter)}
			/>
			
			{errorMessage && <FieldErrorMessage message={errorMessage} />}
		</Wrapper>
	);
};

export default PlanSelector;
